import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames/bind";
import styles from "./AdminDashboardPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faSignOutAlt, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import CustomerHistory from "./CustomerHistory";
import RedeemPoints from "./RedeemPoints";
import TransactionHistory from "./TransactionHistory";
import AddPoint from "./AddPoint";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { useNavigate } from "react-router-dom";
import { BASE_AUTH_URL, BASE_TRANSACTIONS_URL } from "~/helper/apiService";
import DriverManager from "~/page/DriverManager";
import CheckInManager from "../CheckInManager/CheckInManager";
import WashManager from "../WashManager";
import ImageManager from "../ImageManager";
import PromotionManagement from "../PromotionManagement";
import RewardManagement from "../RewardManagement";
import VoucherManagement from "../VoucherManagement";
import TripsManagement from "../TripsManagement";

const cx = classNames.bind(styles);

const menu = {
  pointManagement: {
    label: "Quản lý điểm",
    items: [
      {
        key: "TransactionHistory",
        label: "Lịch sử giao dịch",
        component: <TransactionHistory />,
        protected: true,
      },
      {
        key: "CustomerHistory",
        label: "Tra cứu",
        component: <CustomerHistory />,
      },
      {
        key: "RedeemPoints",
        label: "Đổi điểm",
        component: <RedeemPoints />,
        protected: true,
      },
      {
        key: "AddPoint",
        label: "Thêm điểm",
        component: <AddPoint />,
      },
    ],
  },
  promotions: {
    label: "Ưu đãi",
    items: [
      {
        key: "PromotionManagement",
        label: "Giờ vàng",
        component: <PromotionManagement />,
      },
      {
        key: "RewardManagement",
        label: "Quay thưởng",
        component: <RewardManagement />,
      },
    ],
  },
  mobileApp: {
    label: "Mobile App",
    items: [
      {
        key: "VoucherAppManagement",
        label: "Voucher",
        component: <VoucherManagement />,
      },
      {
        key: "TripsManagement",
        label: "Chuyến đi",
        component: <TripsManagement />,
      },
    ],
  },
  driver: {
    label: "Tài xế",
    items: [
      {
        key: "DriverManager",
        label: "Tài xế",
        component: <DriverManager />,
      },
      {
        key: "CheckInManager",
        label: "Check-in",
        component: <CheckInManager />,
      },
      {
        key: "WashManager",
        label: "Rửa xe",
        component: <WashManager />,
      },
      {
        key: "DeleteImages",
        label: "Dọn ảnh",
        component: <ImageManager />,
      },
    ],
  },
};

function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState("TransactionHistory");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({ pointManagement: true });
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);
  const navigate = useNavigate();

  const getItemByKey = (key) => {
    for (const groupKey in menu) {
      const item = menu[groupKey].items.find((i) => i.key === key);
      if (item) return item;
    }
    return null;
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BASE_AUTH_URL}check-login`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem("user", JSON.stringify(data.user));
          if (data.user.role === "admin") {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            navigate("/login");
          }
        } else {
          setIsAuthenticated(false);
          localStorage.removeItem("user");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error checking auth:", error);
        localStorage.removeItem("user");
        setIsAuthenticated(false);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      const response = await fetch(`${BASE_AUTH_URL}logout`, {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message, "success");
        setIsAuthenticated(false);
        setActiveTab("TransactionHistory");
        navigate("/login");
      } else {
        showToast(data.error || "Đăng xuất thất bại", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    }
  };

  const confirmLogout = () => {
    showModal({
      title: "Xác Nhận Đăng Xuất",
      message: "Bạn có chắc chắn muốn đăng xuất?",
      onConfirm: handleLogout,
      onCancel: null,
    });
  };

  const handleTabChange = (key) => {
    const item = getItemByKey(key);
    if (item && item.protected && !isAuthenticated) {
      showToast("Vui lòng đăng nhập để truy cập tính năng này", "error");
      navigate("/login");
    } else {
      setActiveTab(key);
      setIsSidebarOpen(false);
    }
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  if (loading) {
    return <div className={cx("loading")}>Đang kiểm tra xác thực...</div>;
  }

  const currentItem = getItemByKey(activeTab);

  return (
    <div className={cx("container")}>
      <button
        className={cx("hamburger", { active: isSidebarOpen })}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <FontAwesomeIcon icon={faBars} className={cx("hamburger-icon")} />
      </button>
      <div
        className={cx("overlay", { active: isSidebarOpen })}
        onClick={() => setIsSidebarOpen(false)}
      ></div>
      <div className={cx("sidebar", { open: isSidebarOpen })}>
        <h2 className={cx("sidebar-title")}>Quản Lý Điểm</h2>
        <ul className={cx("sidebar-menu")}>
          {Object.keys(menu).map((groupKey) => (
            <>
              <li
                key={`header-${groupKey}`}
                className={cx("group-header")}
                onClick={() => toggleGroup(groupKey)}
              >
                <span>{menu[groupKey].label}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={cx("chevron-icon", { expanded: expandedGroups[groupKey] })}
                />
              </li>
              <ul
                key={`submenu-${groupKey}`}
                className={cx("submenu", { open: expandedGroups[groupKey] })}
              >
                {menu[groupKey].items.map((item) => (
                  <li
                    key={item.key}
                    className={cx("sidebar-item", { active: activeTab === item.key })}
                    onClick={() => handleTabChange(item.key)}
                  >
                    {item.label}
                  </li>
                ))}
              </ul>
            </>
          ))}
          {isAuthenticated && (
            <li className={cx("sidebar-item")} onClick={confirmLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} style={{ marginRight: "10px" }} />
              Đăng Xuất
            </li>
          )}
        </ul>
      </div>
      <div className={cx("content")}>
        <h1 className={cx("title")}>{currentItem?.label}</h1>
        {currentItem?.component}
      </div>
    </div>
  );
}

export default AdminDashboardPage;